<template>
  <Dialog
    :open="modelValue"
    bare
    size="sm"
    @update:open="$emit('update:modelValue', $event)"
  >
    <VisuallyHidden>
      <DialogTitle>About Frappe Playground</DialogTitle>
      <DialogDescription>Information about the playground, including installed apps and documentation.</DialogDescription>
    </VisuallyHidden>
    <div class="bg-surface-elevation-1 rounded-6 shadow-xl overflow-hidden flex flex-col relative text-left">
      <Button
        variant="ghost"
        aria-label="Close"
        class="absolute top-2 right-2 z-10 w-8"
        @click="$emit('update:modelValue', false)"
      >
        <template #icon>
          <X class="h-4 w-4 text-ink-gray-9" />
        </template>
      </Button>

      <div class="p-5 pb-0">
        <div class="flex flex-col items-center justify-center pt-3 pb-4">
          <div class="mb-4 flex items-center justify-center">
            <BrandIcon class="h-14 w-14" clipId="info-dialog-clip" />
          </div>
          <div class="flex items-center gap-2">
            <span class="text-lg font-semibold text-ink-gray-9">Frappe Playground</span>
            <Badge theme="gray" variant="subtle" size="sm" class="font-mono font-medium">v{{ pkg.version }}</Badge>
          </div>
        </div>

        <Tabs :tabs="[{ label: 'About', value: 'About' }, { label: 'Credentials', value: 'Credentials' }, { label: 'Insights', value: 'Insights' }]" v-model="activeTab" class="w-full">
          <template #tab-panel="{ tab }">
            <div v-if="tab.label === 'About'" class="pt-4 min-h-[220px] flex flex-col gap-1">
              <a
                v-for="link in aboutLinks"
                :key="link.label"
                :href="link.url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-between p-2 -mx-2 rounded-4 hover:bg-surface-gray-2 transition-colors text-ink-gray-9 text-sm"
              >
                <div class="flex items-center gap-3">
                  <component :is="link.icon" class="w-4 h-4 text-ink-gray-6 stroke-[1.5]" />
                  <span class="font-medium">{{ link.label }}</span>
                </div>
                <ArrowRight class="w-4 h-4 text-ink-gray-5 stroke-[1.5]" />
              </a>
            </div>
            <div v-if="tab.label === 'Credentials'" class="pt-4 min-h-[220px] flex flex-col gap-4">
              <p class="text-[13px] text-ink-gray-5 leading-relaxed">
                Use these default credentials to log in to the Admin Desk.
              </p>
              <div class="flex flex-col gap-3">
                <FormControl
                  type="text"
                  label="Username"
                  :modelValue="LOGIN_DEMO.username"
                  disabled
                >
                  <template #prefix>
                    <User class="w-4 h-4 text-ink-gray-5" />
                  </template>
                  <template #suffix>
                    <Button variant="ghost" aria-label="Copy username" @click="copyUsername(LOGIN_DEMO.username)">
                      <template #icon>
                        <Check v-if="copiedUsername" class="w-4 h-4 text-green-600" />
                        <Copy v-else class="w-4 h-4 text-ink-gray-5" />
                      </template>
                    </Button>
                  </template>
                </FormControl>
                <FormControl
                  type="text"
                  label="Password"
                  :modelValue="LOGIN_DEMO.password"
                  disabled
                >
                  <template #prefix>
                    <Key class="w-4 h-4 text-ink-gray-5" />
                  </template>
                  <template #suffix>
                    <Button variant="ghost" aria-label="Copy password" @click="copyPassword(LOGIN_DEMO.password)">
                      <template #icon>
                        <Check v-if="copiedPassword" class="w-4 h-4 text-green-600" />
                        <Copy v-else class="w-4 h-4 text-ink-gray-5" />
                      </template>
                    </Button>
                  </template>
                </FormControl>
              </div>
            </div>
            <div v-if="tab.label === 'Insights'" class="pt-4 min-h-[220px] flex flex-col gap-3 text-left">
              <div v-for="(step, idx) in bootSteps" :key="idx" class="flex items-center justify-between gap-3 text-[13px] text-ink-gray-8">
                <div class="flex items-center gap-3">
                  <Rocket v-if="idx === bootSteps.length - 1" class="h-4 w-4 shrink-0 text-blue-600" />
                  <CheckCircle2 v-else class="h-4 w-4 shrink-0 text-green-600" />
                  <span>{{ step.label }}</span>
                </div>
                <span v-if="step.elapsed !== null" class="text-xs text-ink-gray-5 tabular-nums font-mono opacity-70">
                  {{ formatElapsed(step.elapsed) }}
                </span>
              </div>
              <div class="flex items-center justify-between text-[13px] font-medium text-ink-gray-9">
                <div class="flex items-center gap-3">
                  <Coffee class="h-4 w-4 shrink-0 text-amber-700" />
                  <span>Brewed in</span>
                </div>
                <span class="tabular-nums font-mono opacity-80">{{ formatElapsed(totalElapsedMs) }}</span>
              </div>
            </div>
          </template>
        </Tabs>
      </div>

      <a
        href="https://lubus.in"
        target="_blank"
        rel="noopener noreferrer"
        class="block px-5 py-3 border-t border-surface-gray-2 bg-surface-gray-50 text-center text-[13px] text-ink-gray-5 hover:text-ink-gray-9 hover:underline transition-colors group"
      >
        \ Made by lubus /
      </a>
    </div>
  </Dialog>
</template>

<script setup>
import { Badge, Button, Dialog, Tabs, FormControl } from 'frappe-ui'
import { ref, computed } from 'vue'
import { DialogTitle, DialogDescription, VisuallyHidden } from 'reka-ui'
import { User, Key, CheckCircle2, Rocket, Coffee, X, Bug, Heart, Headphones, ArrowRight, Copy, Check, BookOpen } from '@lucide/vue'
import GithubIcon from './GithubIcon.vue'
import BrandIcon from './BrandIcon.vue'
import { LOGIN_DEMO } from '../playground/config.js'
import pkg from '../../../../package.json'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  bootSteps: {
    type: Array,
    default: () => [],
  }
})

defineEmits(['update:modelValue'])

const activeTab = ref('About')

const copiedUsername = ref(false)
const copiedPassword = ref(false)

async function copyUsername(text) {
  try {
    await navigator.clipboard.writeText(text)
    copiedUsername.value = true
    setTimeout(() => { copiedUsername.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy', err)
  }
}

async function copyPassword(text) {
  try {
    await navigator.clipboard.writeText(text)
    copiedPassword.value = true
    setTimeout(() => { copiedPassword.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy', err)
  }
}

const aboutLinks = [
  { label: 'Documentation', icon: BookOpen, url: '/docs/' },
  { label: 'GitHub', icon: GithubIcon, url: 'https://github.com/lubusIN/frappe-playground' },
  { label: 'Submit Feedback', icon: Bug, url: 'https://github.com/lubusIN/frappe-playground/issues' },
  { label: 'Buy us a coffee', icon: Heart, url: 'https://github.com/sponsors/lubusIN' },
  { label: 'Get in touch', icon: Headphones, url: 'https://lubus.in' },
]

const totalElapsedMs = computed(() => {
  return props.bootSteps.reduce((total, step) => total + (step.elapsed || 0), 0)
})

function formatElapsed(ms) {
  if (ms == null) return ''
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}
</script>
